import type { DietTypes, Ingredient, InstructionStep, MealType, Recipe, RecipeCategory } from '../types';
import { ingredientTextsMatchAllergy, type KnownAllergyId } from './allergies';
import { DIET_TYPE_KEYS, EMPTY_DIET_TYPES, normalizeDietTypes } from './dietTypes';

const MEAL_TYPES: MealType[] = ['Breakfast', 'Lunch', 'Dinner'];
import { FIRST_COURSE_KEYWORDS, RECIPE_CATEGORIES } from './recipeCategories';

export type RecipeFilterEstimateInput = Pick<
    Recipe,
    'name' | 'prepTime' | 'ingredients' | 'category' | 'instructions' | 'suitableFor' | 'dietTypes' | 'isKeto'
>;

const MEAT_KEYWORDS = [
    'chicken', 'beef', 'pork', 'lamb', 'turkey', 'bacon', 'ham', 'sausage', 'steak', 'veal', 'duck', 'goose',
    'prosciutto', 'pepperoni', 'salami', 'meatball', 'meat', 'liver', 'kidney', 'ground beef', 'mince',
    'עוף', 'בקר', 'בשר', 'כבש', 'הודו', 'חזיר', 'נקניק', 'קבב', 'כבד', 'אנטריקוט', 'שווארמה',
];

const HONEY_KEYWORDS = ['honey', 'דבש'];
const GELATIN_KEYWORDS = ['gelatin', 'gelatine', 'ג\'לטין'];

const HIGH_CARB_KEYWORDS = [
    'sugar', 'flour', 'bread', 'pasta', 'noodle', 'rice', 'potato', 'corn starch', 'cornstarch', 'starch',
    'pastry', 'dough', 'bun', 'roll', 'tortilla', 'pita', 'couscous', 'quinoa', 'barley', 'oat', 'oats',
    'maple syrup', 'molasses', 'corn syrup', 'honey', 'date syrup', 'silan',
    'סוכר', 'קמח', 'לחם', 'פסטה', 'אורז', 'תפוח אדמה', 'תירס', 'בצק', 'פita', 'פתית',
];

const LEGUME_KEYWORDS = [
    'bean', 'beans', 'lentil', 'lentils', 'chickpea', 'chickpeas', 'pea', 'peas', 'peanut', 'soy', 'tofu',
    'פול', 'עדש', 'חומוס', 'אפונה', 'בוטן', 'סויה', 'טופו',
];

const BREAKFAST_KEYWORDS = [
    'breakfast', 'pancake', 'waffle', 'omelette', 'omelet', 'oatmeal', 'porridge', 'cereal', 'granola',
    'french toast', 'smoothie bowl', 'shakshuka', 'bagel', 'muffin', 'croissant',
    'בוקר', 'ארוחת בוקר', 'פנקייק', 'וaffle', 'שיבולת', 'גרנולה', 'חביתה', 'שקשuka', 'בייגל', 'מאפין',
];

const LUNCH_KEYWORDS = [
    'lunch', 'sandwich', 'wrap', 'panini', 'quesadilla', 'taco', 'burrito', 'soup and salad',
    'צהריים', 'ארוחת צהריים', 'סנדwich', 'סלט', 'מרק', 'טוסט',
];

const DINNER_KEYWORDS = [
    'dinner', 'supper', 'roast', 'stew', 'casserole', 'curry', 'steak night',
    'ערב', 'ארוחת ערב', 'תבשיל', 'צלי', 'נזיד',
];

const DESSERT_KEYWORDS = [
    'cake', 'cookie', 'brownie', 'pie', 'tart', 'pudding', 'ice cream', 'mousse', 'cupcake',
    'עוג', 'ביסקוויט', 'עוגיות', 'טארט', 'פuding', 'גלידה', 'מousse',
];

function normalizeText(value: string): string {
    return value.toLowerCase().trim();
}

function includesKeyword(text: string, keywords: readonly string[]): boolean {
    const normalized = normalizeText(text);
    return keywords.some((keyword) => normalized.includes(normalizeText(keyword)));
}

function collectIngredientNames(recipe: RecipeFilterEstimateInput): string[] {
    const names: string[] = [];
    for (const ingredient of recipe.ingredients) {
        if (ingredient.name.trim()) {
            names.push(normalizeText(ingredient.name));
        }
    }
    return names;
}

function collectRecipeSearchText(recipe: RecipeFilterEstimateInput): string {
    const ingredientText = recipe.ingredients.map((ingredient) => ingredient.name).join(' ');
    let instructionText = '';
    if (typeof recipe.instructions === 'string') {
        instructionText = recipe.instructions;
    } else if (Array.isArray(recipe.instructions)) {
        instructionText = recipe.instructions.map((step) => step.content).join(' ');
    }
    return normalizeText(`${recipe.name} ${ingredientText} ${instructionText}`);
}

function ingredientMatchesKeyword(ingredient: string, keyword: string): boolean {
    const normalizedKeyword = normalizeText(keyword);
    if (!normalizedKeyword) return false;
    if (ingredient.includes(normalizedKeyword)) return true;
    return normalizedKeyword.length >= 4
        && ingredient.split(/\s+/).some((word) => word.startsWith(normalizedKeyword));
}

function hasKeywordInIngredients(ingredients: string[], keywords: readonly string[]): boolean {
    return ingredients.some((ingredient) =>
        keywords.some((keyword) => ingredientMatchesKeyword(ingredient, keyword)),
    );
}

function hasAllergenKeywords(ingredients: string[], allergyId: KnownAllergyId): boolean {
    return ingredientTextsMatchAllergy(ingredients, allergyId);
}

function hasMeat(recipe: RecipeFilterEstimateInput, ingredients: string[]): boolean {
    if (recipe.ingredients.some((ingredient) => ingredient.isMeat)) {
        return true;
    }
    if (hasKeywordInIngredients(ingredients, MEAT_KEYWORDS)) {
        return true;
    }
    return hasAllergenKeywords(ingredients, 'fish')
        || hasAllergenKeywords(ingredients, 'shellfish')
        || hasAllergenKeywords(ingredients, 'molluscs');
}

function hasDairy(ingredients: string[]): boolean {
    return hasAllergenKeywords(ingredients, 'milk');
}

function hasEggs(ingredients: string[]): boolean {
    return hasAllergenKeywords(ingredients, 'eggs');
}

function hasGluten(ingredients: string[]): boolean {
    return hasAllergenKeywords(ingredients, 'wheat') || hasAllergenKeywords(ingredients, 'gluten');
}

function hasHoney(ingredients: string[], text: string): boolean {
    return includesKeyword(text, HONEY_KEYWORDS) || hasKeywordInIngredients(ingredients, HONEY_KEYWORDS);
}

function hasGelatin(ingredients: string[], text: string): boolean {
    return includesKeyword(text, GELATIN_KEYWORDS) || hasKeywordInIngredients(ingredients, GELATIN_KEYWORDS);
}

function hasHighCarb(ingredients: string[], text: string): boolean {
    return includesKeyword(text, HIGH_CARB_KEYWORDS) || hasKeywordInIngredients(ingredients, HIGH_CARB_KEYWORDS);
}

function hasLegumes(ingredients: string[], text: string): boolean {
    return includesKeyword(text, LEGUME_KEYWORDS) || hasKeywordInIngredients(ingredients, LEGUME_KEYWORDS);
}

function hasGrains(ingredients: string[], text: string): boolean {
    return hasGluten(ingredients)
        || includesKeyword(text, ['rice', 'corn', 'quinoa', 'bulgur', 'couscous', 'אורז', 'תירס']);
}

export function normalizeSuitableFor(value: unknown): MealType[] {
    if (!Array.isArray(value)) return [];

    const meals = value.filter(
        (meal): meal is MealType => typeof meal === 'string' && MEAL_TYPES.includes(meal as MealType),
    );

    return meals;
}

export function estimateDietTypes(recipe: RecipeFilterEstimateInput): DietTypes {
    const ingredients = collectIngredientNames(recipe);
    const text = collectRecipeSearchText(recipe);
    const meat = hasMeat(recipe, ingredients);
    const dairy = hasDairy(ingredients);
    const eggs = hasEggs(ingredients);
    const gluten = hasGluten(ingredients);
    const honey = hasHoney(ingredients, text);
    const gelatin = hasGelatin(ingredients, text);
    const highCarb = hasHighCarb(ingredients, text);
    const legumes = hasLegumes(ingredients, text);
    const grains = hasGrains(ingredients, text);

    const stored = normalizeDietTypes(recipe.dietTypes, recipe.isKeto);

    return {
        isKeto: stored.isKeto || (!highCarb && !grains && !legumes && ingredients.length > 0),
        isVegetarian: !meat,
        isVegan: !meat && !dairy && !eggs && !honey && !gelatin,
        isGlutenFree: !gluten,
        isPaleo: !grains && !legumes && !dairy && !highCarb,
        isLowCarb: !highCarb,
        isDairyFree: !dairy,
    };
}

export function mergeEstimatedDietTypes(stored: DietTypes, estimated: DietTypes): DietTypes {
    const merged = { ...EMPTY_DIET_TYPES };
    for (const key of DIET_TYPE_KEYS) {
        merged[key] = stored[key] === true || estimated[key] === true;
    }
    return merged;
}

export function recipeHasDietType(
    recipe: RecipeFilterEstimateInput,
    dietKey: keyof DietTypes,
): boolean {
    const stored = normalizeDietTypes(recipe.dietTypes, recipe.isKeto);
    if (stored[dietKey]) return true;
    return estimateDietTypes(recipe)[dietKey];
}

export function estimateSuitableFor(recipe: RecipeFilterEstimateInput): MealType[] {
    const text = collectRecipeSearchText(recipe);
    const meals = new Set<MealType>();

    if (includesKeyword(text, BREAKFAST_KEYWORDS)) meals.add('Breakfast');
    if (includesKeyword(text, LUNCH_KEYWORDS)) meals.add('Lunch');
    if (includesKeyword(text, DINNER_KEYWORDS)) meals.add('Dinner');

    if (recipe.category === 'Dessert' || includesKeyword(text, DESSERT_KEYWORDS)) {
        meals.add('Dinner');
        meals.add('Lunch');
    }
    if (recipe.category === 'Snack') {
        meals.add('Lunch');
    }
    if (recipe.category === 'First') {
        meals.add('Dinner');
        meals.add('Lunch');
    }
    if (recipe.category === 'Side' || recipe.category === 'Spread') {
        meals.add('Dinner');
    }
    if (recipe.category === 'Meal') {
        meals.add('Dinner');
    }

    if (meals.size === 0) {
        meals.add('Dinner');
    }

    return MEAL_TYPES.filter((meal) => meals.has(meal));
}

export function mergeEstimatedSuitableFor(stored: MealType[], estimated: MealType[]): MealType[] {
    const merged = new Set<MealType>([...stored, ...estimated]);
    const ordered = MEAL_TYPES.filter((meal) => merged.has(meal));
    return ordered.length > 0 ? ordered : ['Dinner'];
}

export function getRecipeSuitableFor(recipe: RecipeFilterEstimateInput): MealType[] {
    const stored = normalizeSuitableFor(recipe.suitableFor);
    if (stored.length === 0) {
        return estimateSuitableFor(recipe);
    }
    return mergeEstimatedSuitableFor(stored, estimateSuitableFor(recipe));
}

export function recipeSuitableForMealType(recipe: RecipeFilterEstimateInput, mealType: MealType): boolean {
    return getRecipeSuitableFor(recipe).includes(mealType);
}

export function estimateRecipeCategory(recipe: RecipeFilterEstimateInput): RecipeCategory {
    if (recipe.category && RECIPE_CATEGORIES.includes(recipe.category)) {
        return recipe.category;
    }

    const text = collectRecipeSearchText(recipe);
    if (includesKeyword(text, DESSERT_KEYWORDS) || recipe.category === 'Dessert') {
        return 'Dessert';
    }
    if (includesKeyword(text, FIRST_COURSE_KEYWORDS) || recipe.category === 'First') {
        return 'First';
    }
    if (includesKeyword(text, ['snack', 'חטיף', 'נשנוש'])) {
        return 'Snack';
    }
    if (includesKeyword(text, ['side dish', 'side', 'תוספת', 'ליווי'])) {
        return 'Side';
    }
    if (includesKeyword(text, ['spread', 'dip', 'ממרח', 'רטב'])) {
        return 'Spread';
    }
    if (recipe.ingredients.length >= 4) {
        return 'Meal';
    }
    return 'General';
}

export function getResolvedRecipeDietTypes(recipe: RecipeFilterEstimateInput): DietTypes {
    const stored = normalizeDietTypes(recipe.dietTypes, recipe.isKeto);
    return mergeEstimatedDietTypes(stored, estimateDietTypes(recipe));
}

export function buildAutoTaggedRecipeMetadata(recipe: RecipeFilterEstimateInput): {
    dietTypes: DietTypes;
    suitableFor: MealType[];
    category: RecipeCategory;
} {
    const storedDiet = normalizeDietTypes(recipe.dietTypes, recipe.isKeto);
    const storedMeals = normalizeSuitableFor(recipe.suitableFor);

    return {
        dietTypes: mergeEstimatedDietTypes(storedDiet, estimateDietTypes(recipe)),
        suitableFor: mergeEstimatedSuitableFor(storedMeals, estimateSuitableFor(recipe)),
        category: estimateRecipeCategory(recipe),
    };
}

export function instructionStepCount(instructions: string | InstructionStep[] | undefined): number {
    if (!instructions) return 0;
    if (typeof instructions === 'string') {
        return instructions.split('\n').map((line) => line.trim()).filter(Boolean).length;
    }
    return instructions.filter((step) => step.type === 'step').length;
}

export function collectRecipeIngredientTexts(recipe: {
    ingredients: Pick<Ingredient, 'name'>[];
    englishTranslation?: { ingredients?: Pick<Ingredient, 'name'>[] } | null;
}): string[] {
    const names: string[] = [];
    for (const ingredient of recipe.ingredients) {
        if (ingredient.name.trim()) names.push(normalizeText(ingredient.name));
    }
    const translated = recipe.englishTranslation?.ingredients;
    if (Array.isArray(translated)) {
        for (const ingredient of translated) {
            if (ingredient.name.trim()) names.push(normalizeText(ingredient.name));
        }
    }
    return names;
}
